import i18n from 'lib/i18n';

/**
 * Returns the declarative menu definition for the cportal_user_navigation slug.
 * Rendered by HeaderMenu in fi-live's cportal and attendee layouts.
 *
 * All permission evaluation and URL template resolution happen at render time via
 * filterMenu + PermissionManager (see menuHelper.js).
 *
 * URL templates resolved by filterMenu:
 *   {{slug}}         — org slug
 *   {{user_name}}    — user's full name (title template, not URL)
 *
 * App-switcher sub-items: menuHelper replaces {{slug}} and then overrides
 * current_rule_set_name with the correct target role set per item.
 *
 * @param {object} environment - runtime values
 * @param {string} environment.dashifiDomain  — main cportal domain (MENU_DOMAIN_FOR_FRONT_END)
 * @param {string} environment.adminifiDomain  — only used for fallback logout URL
 * @param {string} environment.logoutPath  — Rails destroy_user_session_path (DELETE)
 */
export const cportalUserNavigation = ({ dashifiDomain, adminifiDomain, logoutPath }) => ({
  slug: 'cportal_user_navigation',
  submenu_items: [
    {
      id: '',
      slug: '',
      title: i18n.t('Home'),
      icon: 'house-chimney',
      font_family: '',
      css_class: '',
      permissions: [{ has_rule_set: ['advisors'] }],
      target_url: `${dashifiDomain}/{{slug}}/cportal/actionable_dashboard`,
      target_method: '',
      new_window: false,
      subsection: false,
      submenu_items: [],
      weight: 100,
    },
    {
      id: 'app-switcher',
      slug: 'app-switcher',
      title: i18n.t('Engage'),
      icon: 'memory',
      font_family: '',
      css_class: '',
      target_url: '',
      target_method: '',
      new_window: false,
      subsection: true,
      weight: 100,
      submenu_items: [
        {
          id: 'train',
          slug: 'train',
          title: i18n.t('Train'),
          icon: '',
          font_family: '',
          css_class: '',
          permissions: [{ has_rule_set: ['he_cc_fs_scorm_manager_names'] }],
          target_url: `${dashifiDomain}/{{slug}}/cportal/actionable_dashboard`,
          target_method: '',
          new_window: false,
          subsection: false,
          submenu_items: [],
          weight: 100,
        },
        {
          id: 'impact',
          slug: 'impact',
          title: i18n.t('Impact'),
          icon: '',
          font_family: '',
          css_class: '',
          permissions: [{ has_capabilities: ['ImpactDashboard'], has_rule_set: ['executive'] }],
          target_url: `${dashifiDomain}/{{slug}}/ceos/impact-dashboard`,
          target_method: '',
          new_window: false,
          subsection: false,
          submenu_items: [],
          weight: 100,
        },
        {
          id: 'learn',
          slug: 'learn',
          title: i18n.t('Learn'),
          icon: '',
          font_family: '',
          css_class: '',
          permissions: [{ has_rule_set: ['he_cc_fs_learner_names'] }],
          target_url: `${dashifiDomain}/{{slug}}/student/dashboard`,
          target_method: '',
          new_window: false,
          subsection: false,
          submenu_items: [],
          weight: 100,
        },
        {
          id: 'promote-advisor',
          slug: 'promote-advisor',
          title: i18n.t('Promote'),
          icon: '',
          font_family: '',
          css_class: '',
          permissions: [{ has_rule_set: ['advisors'] }],
          target_url: `${dashifiDomain}/{{slug}}/cportal/actionable_dashboard`,
          target_method: '',
          new_window: false,
          subsection: false,
          submenu_items: [],
          weight: 100,
        },
        {
          id: 'promote-next',
          slug: 'promote-next',
          title: i18n.t('Promote'),
          icon: '',
          font_family: '',
          css_class: '',
          permissions: [{ has_rule_set: [{ next_managers: 'at_work_manager' }] }],
          target_url: `${dashifiDomain}/{{slug}}/cportal/financial_launchpad`,
          target_method: '',
          new_window: false,
          subsection: false,
          submenu_items: [],
          weight: 100,
        },
        {
          id: 'engage',
          slug: 'engage',
          title: i18n.t('Engage'),
          icon: '',
          font_family: '',
          css_class: '',
          permissions: [{ has_rule_set: ['engage_managers'] }],
          target_url: `${dashifiDomain}/{{slug}}/cportal/actionable_dashboard`,
          target_method: '',
          new_window: false,
          subsection: false,
          submenu_items: [],
          weight: 100,
        },
        {
          id: 'workshop',
          slug: 'workshop',
          title: i18n.t('Engage'),
          icon: '',
          font_family: '',
          css_class: '',
          permissions: [{ has_rule_set: ['workshop_managers'] }],
          target_url: `${dashifiDomain}/{{slug}}/cportal/topics`,
          target_method: '',
          new_window: false,
          subsection: false,
          submenu_items: [],
          weight: 100,
        },
      ],
    },
    {
      id: '',
      slug: '',
      title: '{{user_name}}',
      icon: 'circle-user',
      font_family: '',
      css_class: 'account',
      target_url: '',
      target_method: '',
      new_window: false,
      subsection: false,
      weight: 100,
      submenu_items: [
        {
          id: '',
          slug: '',
          title: i18n.t('My Profile'),
          icon: '',
          font_family: '',
          css_class: '',
          target_url: `${dashifiDomain}/{{slug}}/cportal/registrations/edit`,
          target_method: '',
          new_window: false,
          subsection: false,
          submenu_items: [],
          weight: 100,
        },
        {
          id: 'logout',
          slug: 'logout',
          title: i18n.t('Logout'),
          icon: '',
          font_family: '',
          css_class: '',
          target_url: logoutPath,
          target_method: 'delete',
          new_window: false,
          subsection: false,
          submenu_items: [],
          weight: 100,
        },
        {
          id: '',
          slug: '',
          title: '',
          icon: '',
          font_family: '',
          css_class: '',
          target_url: '',
          target_method: '',
          new_window: false,
          subsection: true,
          weight: 100,
          submenu_items: [
            {
              id: '',
              slug: '',
              title: i18n.t('Help'),
              icon: 'circle-question',
              font_family: '',
              css_class: '',
              help_url_post_processing: true,
              target_url: 'https://help.everfi.com/s/help',
              target_method: '',
              new_window: true,
              subsection: false,
              submenu_items: [],
              weight: 100,
            },
            {
              id: '',
              slug: '',
              title: i18n.t('Terms of Service'),
              icon: 'shield-check',
              font_family: '',
              css_class: '',
              target_url: 'https://everfi.com/terms-of-service',
              target_method: '',
              new_window: true,
              subsection: false,
              submenu_items: [],
              weight: 100,
            },
          ],
        },
      ],
    },
  ],
});
